import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Injector } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { BaseStitchComponent } from '@modules/common/components/base-stitch/base-stitch.component';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { DragDataTypes, type DragData, isNotAlreadyLinked } from '@modules/drag-n-drop/types/drag-data';
import { StitchBottomBarComponent } from '@modules/elements/components/stitch-bottom-bar/stitch-bottom-bar.component';
import { TrashActionComponent } from '@modules/elements/components/stitch-trash-action/stitch-trash-action.component';
import { QuickActionComponent } from '@modules/form-controls/components/quick-action/quick-action.component';
import { UtilsModule } from '@modules/utils/utils.module';
import { validateNativeDragBatch } from '@sharedlib/common/boards/native-drag-payload';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { type FormSummaryDto } from '@sharedlib/dtos/dto/forms-app/form.dto';
import { FormContextMenuDirective } from '../menus/form-context-menu.directive';
import { FormsStitchService } from '../services/forms.service';

/** Native BaseStitch row: selection, drag payload, actions and linked-item bar retain their shared owners. */
@Component({
  selector: 'app-forms-library-card', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './forms-library-card.component.html',
  styleUrls: ['../../common/components/base-stitch/base-stitch.component.less', '../../elements/components/note/note.component.less'],
  imports: [CommonModule, IconComponent, DateFormatPipe, DragNDropModule, FormContextMenuDirective,
    StitchBottomBarComponent, TrashActionComponent, QuickActionComponent, MatTooltipModule, UtilsModule],
})
export class FormsLibraryCardComponent extends BaseStitchComponent<StitchType.FORM, FormSummaryDto> {
  protected moveDragDataTypes: DragDataTypes[] = [];
  protected dragDataType = DragDataTypes.form;
  protected override containerIdField = 'folderUid';
  constructor() { super(inject(Injector), inject(FormsStitchService)); }
  public override dndPredicate = (data: DragData): boolean => {
    if (!this.item || this.item.deleted || !data?.data?.length) return false;
    if ([DragDataTypes.tag, DragDataTypes.knot, DragDataTypes.connection].some(type => type === data.type))
      return isNotAlreadyLinked(data, this.linkedInfo) && data.data.length <= 150 && data.data.every(item => 'name' in item && typeof item.name === 'string' && !!item.name.trim());
    const sources = this.sources(data);
    return sources.data.length > 0 && isNotAlreadyLinked(sources, this.linkedInfo) && validateNativeDragBatch(sources.data, { purpose: 'stitch', spaceId: this.item.accountId, target: this.item }).ok;
  };
  /** BoardDropSources keeps the rest of a multi-selection when it includes its drop target. */
  private sources(data: DragData): DragData {
    return data.type === DragDataTypes.form ? { ...data, data: data.data.filter(item => !(item.id === this.item.id && 'itemType' in item && item.itemType === StitchType.FORM)) } : data;
  }
  public override handleStitch(data: DragData): void { if (this.dndPredicate(data)) super.handleStitch(this.sources(data)); }
  public override handleMove(): void { throw Error('A form row stitches items; filing belongs to its folder target.'); }
}
